/**
 * Friendly "staging is asleep" notice.
 *
 * Staging powers down outside business hours (see
 * `infra/terraform/envs/staging/scheduler.tf` + `util/stagingHours`), so a
 * user who left a tab open past 5pm — or hits staging in the evening —
 * would otherwise see a raw connection error. This catches the API
 * transport failure globally and, *only on staging and only outside
 * business hours*, shows an explanatory overlay instead.
 *
 * Within business hours a transport failure is a real outage, so we stay
 * out of the way and let the app's normal error handling surface it. The
 * notice also clears itself on the next successful response (the box is
 * back).
 */
import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import BedtimeRoundedIcon from '@mui/icons-material/BedtimeRounded';

import { registerNetworkHandlers } from '../api/client';
import {
  STAGING_HOURS_LABEL,
  isOutsideStagingHours,
  isStagingHost,
} from '../util/stagingHours';

export function StagingOffHoursNotice() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Never arm anywhere but the staging deployment.
    if (!isStagingHost()) return;

    registerNetworkHandlers({
      onError: () => {
        // Re-check the clock at failure time so a tab left open across
        // 5pm flips correctly without a reload.
        if (isOutsideStagingHours()) setOpen(true);
      },
      onRecovered: () => setOpen(false),
    });
    return () =>
      registerNetworkHandlers({ onError: null, onRecovered: null });
  }, []);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogContent>
        <Stack
          spacing={2}
          alignItems="center"
          textAlign="center"
          sx={{ py: 2 }}
        >
          <BedtimeRoundedIcon
            sx={{ fontSize: 48, color: 'text.secondary' }}
          />
          <Typography variant="h6">Staging is asleep</Typography>
          <Typography variant="body2" color="text.secondary">
            This staging environment powers down outside business hours to
            save cost. It&rsquo;s available {STAGING_HOURS_LABEL}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Need it now? Ask an admin to wake it (it takes a few minutes to
            come up), then retry.
          </Typography>
          <Button variant="contained" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
